import { buildApiUrl, buildAssetUrl } from "../../utils/api";
import { getCookie } from "../../utils/cookies";
import toast from "react-hot-toast";

const WISHLIST_BASE = buildApiUrl("api/v1/customer/wishlist");

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getCookie("authToken")}`,
});

/**
 * API response shape (both GET and POST /add return the same structure):
 * {
 *   success: true,
 *   data: {
 *     customer_id: 3,
 *     total_items: 6,
 *     wishlist_items: [ { wishlist_id, product_id, product_name, product_image,
 *                         price, original_price, category_name, ... } ]
 *   }
 * }
 */
const extractItems = (data) => {
  // data can be { total_items, wishlist_items } or a plain array
  if (!data) return { items: [], count: 0 };

  if (Array.isArray(data)) {
    const normalized = data.map(normalizeItem);
    return { items: normalized, count: normalized.length };
  }

  const rawItems = data.wishlist_items ?? data.items ?? [];
  const totalItems = data.total_items ?? rawItems.length;
  return {
    items: rawItems.map(normalizeItem),
    count: typeof totalItems === "number" ? totalItems : rawItems.length,
  };
};

const normalizeItem = (item) => ({
  wishlist_id: item.wishlist_id ?? item.id ?? null,
  product_id: item.product_id,
  name: item.product_name ?? item.name ?? "Product",
  image: buildAssetUrl(item.product_image ?? item.image ?? item.image1 ?? null),
  price: parseFloat(item.price ?? 0),
  originalPrice: parseFloat(item.original_price ?? item.originalPrice ?? 0),
  category: item.category_name ?? "",
  unit: item.unit_name ?? "",
  brand: item.brand_name ?? "",
  variant_id: item.variant_id ?? item.product_variant_id ?? null,
  variant_name: item.variant_name ?? null,
});

export const createWishlistSlice = (set, get) => ({
  wishlist: [],
  wishlist_count: 0,
  wishlist_loading: false,
  wishlist_error: null,

  /* ── helper ── */
  isInWishlist: (productId) =>
    get().wishlist.some(
      (item) => String(item.product_id) === String(productId),
    ),

  /* ── GET /wishlist ── */
  fetchWishlist: async () => {
    if (!getCookie("authToken")) return;
    set({ wishlist_loading: true, wishlist_error: null });
    try {
      const res = await fetch(WISHLIST_BASE, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to fetch wishlist");

      const { items, count } = extractItems(json.data);
      set({ wishlist: items, wishlist_count: count, wishlist_loading: false });
    } catch (err) {
      set({ wishlist_error: err.message, wishlist_loading: false });
    }
  },

  /* ── POST /wishlist/add ── */
  addToWishlist: async (productId) => {
    if (!getCookie("authToken")) return false;
    const pid =
      typeof productId === "object"
        ? (productId.product_id ?? productId.id)
        : productId;

    set({ wishlist_loading: true });
    try {
      const res = await fetch(`${WISHLIST_BASE}/add`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ product_id: pid }),
      });
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to add to wishlist");

      // API returns updated wishlist in the same response — use it directly
      const { items, count } = extractItems(json.data);
      set({ wishlist: items, wishlist_count: count, wishlist_loading: false });
      return true;
    } catch (err) {
      set({ wishlist_error: err.message, wishlist_loading: false });
      toast.error(err.message || "Failed to add to wishlist");
      return false;
    }
  },

  /* ── DELETE /wishlist/remove ── */
  removeFromWishlist: async (productId, variantId = null) => {
    if (!getCookie("authToken")) return false;
    const pid =
      typeof productId === "object"
        ? (productId.product_id ?? productId.id)
        : productId;

    // Optimistic: remove immediately from local state
    set((state) => {
      const updated = state.wishlist.filter(
        (i) => String(i.product_id) !== String(pid),
      );
      return { wishlist: updated, wishlist_count: updated.length };
    });

    try {
      const body = { product_id: pid };
      if (variantId) body.variant_id = variantId;

      const res = await fetch(`${WISHLIST_BASE}/remove`, {
        method: "DELETE",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to remove from wishlist");

      // Sync count from response if available
      if (json.data?.total_items !== undefined) {
        set({ wishlist_count: json.data.total_items });
      }
      return true;
    } catch (err) {
      await get().fetchWishlist(); // revert on failure
      toast.error(err.message || "Failed to remove from wishlist");
      return false;
    }
  },

  /* ── toggle (add if not in list, remove if in list) ── */
  toggleWishlist: async (productId, variantId = null) => {
    if (!getCookie("authToken")) return "auth_required";
    const inList = get().isInWishlist(productId);
    return inList
      ? get().removeFromWishlist(productId, variantId)
      : get().addToWishlist(productId);
  },

  /* ── DELETE /wishlist/clear ── */
  clearWishlist: async () => {
    if (!getCookie("authToken")) return;
    set({ wishlist: [], wishlist_count: 0 });
    try {
      const res = await fetch(`${WISHLIST_BASE}/clear`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to clear wishlist");
      toast.success("Wishlist cleared");
    } catch (err) {
      await get().fetchWishlist();
      toast.error(err.message || "Failed to clear wishlist");
    }
  },
});
